// Chip — small pill used across the app for course tags (Starter / Main /
// Dessert), phase tags (PreCook / PrepCook / LiveCook), the "Verified ✓"
// affordance and lane labels (A0, S1–S3, M1–M3, D1–D3).
//
// Presentational only: single `text` plus `variant` prop. Selection /
// radio semantics live in the consumer (Phase 5+).
//
// See README §6.1 (course chips), §6.2 (verified chip), §6.7.3 (phase
// chips in the overrun banner) and §6.7.4 (lane chips on task
// rectangles).

import type { ReactNode } from 'react';
import { theme } from '../theme';

export type ChipVariant = 'course' | 'phase' | 'verified' | 'lane';

export interface ChipStyle {
  bg: string;
  fg: string;
  border?: string;
}

export interface ChipProps {
  text: string;
  variant: ChipVariant;
  /** Optional aria-label override; defaults to the visible text. */
  ariaLabel?: string;
  /** Optional colour override (sourced from theme) — lets lane chips carry
   *  their per-course colour without bloating the variant enum. Overrides
   *  the variant's bg/fg. */
  tone?: ChipStyle;
  /** Optional trailing glyph (e.g. a clock) rendered after the text in the
   *  chip's flex row. Should be decorative (aria-hidden); set `ariaLabel`
   *  so the chip still conveys meaning to assistive tech. */
  icon?: ReactNode;
}

function styleFor(variant: ChipVariant): ChipStyle {
  switch (variant) {
    case 'course':
      // Quiet oat-on-charcoal pill; visually a tag, not a CTA.
      return { bg: theme.bg.raised, fg: theme.text.primary };
    case 'phase':
      // Used inside the overrun banner (dark surface) and on the prereqs
      // header. Sage fill reads as calm/neutral on either background.
      return { bg: theme.accent.calm, fg: theme.text.primary };
    case 'verified':
      // Terracotta border, charcoal text — a quiet badge of provenance.
      return {
        bg: theme.bg.base,
        fg: theme.text.primary,
        border: theme.accent.action,
      };
    case 'lane':
      // Muted background so lane chips don't compete with task titles.
      return { bg: theme.bg.muted, fg: theme.text.muted };
  }
}

export function Chip({ text, variant, ariaLabel, tone, icon }: ChipProps) {
  const s = tone ?? styleFor(variant);
  // `role="status"` only on `verified` — that chip *is* a status badge
  // ("Verified ✓"). The other variants are presentational labels
  // (course, phase, lane) and a generic <span> reads more honestly to
  // assistive tech. Phase 4 review flagged the redundant roles.
  const isStatus = variant === 'verified';
  return (
    <span
      role={isStatus ? 'status' : undefined}
      aria-label={isStatus ? (ariaLabel ?? text) : ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.2rem 0.6rem',
        borderRadius: '999px',
        fontSize: '0.85rem',
        fontWeight: 500,
        lineHeight: 1.2,
        background: s.bg,
        color: s.fg,
        border: s.border ? `1px solid ${s.border}` : '1px solid transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
      {icon}
    </span>
  );
}

export default Chip;
